import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { FaTimesCircle } from 'react-icons/fa';
import { COLORS } from '../utils';

const TEXT_COLORS = COLORS;
const BACKGROUND_COLORS = [{ hex: 'transparent', title: 'No fill' }, ...COLORS].filter(
  (item) => item.title !== 'Black',
);

interface Props {
  editor: Editor;
}

export const TextColorSelect = (props: Props) => {
  const { editor } = props;

  const changeColor = React.useCallback(
    (color: string) => {
      editor.chain().focus().setColor(color).run();
    },
    [editor],
  );

  const changeBackground = React.useCallback(
    (color: string) => {
      if (color === 'transparent') {
        editor.chain().focus().unsetHighlight().run();
      } else {
        editor.chain().focus().toggleHighlight({ color }).run();
      }
    },
    [editor],
  );

  const reset = React.useCallback(() => {
    editor.chain().focus().unsetColor().unsetHighlight().run();
  }, [editor]);

  const activeTextColor =
    TEXT_COLORS.find((item) => editor.isActive('textStyle', { color: item.hex })) ??
    TEXT_COLORS[0]!;

  const activeBackgroundColor =
    BACKGROUND_COLORS.find((item) => editor.isActive('highlight', { color: item.hex })) ??
    BACKGROUND_COLORS[0]!;

  if (!activeTextColor || !activeBackgroundColor) {
    return null;
  }

  return (
    <FloatingPopover
      placement='bottom'
      render={() => (
        <ChakraV3.Stack
          bg='white'
          p={4}
          w='fit-content'
          rounded='lg'
          shadow='2xl'
          gap={4}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <ChakraV3.Stack>
            <ChakraV3.Text fontSize='xs' fontWeight='semibold' color='secondary'>
              Color
            </ChakraV3.Text>
            <ChakraV3.HStack>
              {TEXT_COLORS.map((item) => (
                <ChakraV3.IconButton
                  onClick={() => changeColor(item.hex)}
                  key={item.title}
                  variant='outline'
                  bg='white'
                  borderWidth={activeTextColor === item ? '2px' : undefined}
                  borderColor={activeTextColor === item ? item.hex : undefined}
                  size='sm'
                  aria-label={item.title}
                  _hover={{
                    borderColor: 'black',
                    borderWidth: '2px',
                  }}
                >
                  <ChakraV3.Text
                    fontWeight='semibold'
                    fontSize='lg'
                    lineHeight='normal'
                    color={item.hex}
                  >
                    A
                  </ChakraV3.Text>
                </ChakraV3.IconButton>
              ))}
            </ChakraV3.HStack>
          </ChakraV3.Stack>
          <ChakraV3.Stack>
            <ChakraV3.Text fontSize='xs' fontWeight='semibold' color='secondary'>
              Background
            </ChakraV3.Text>
            <ChakraV3.HStack>
              {BACKGROUND_COLORS.map((item) => (
                <ChakraV3.IconButton
                  onClick={() => changeBackground(item.hex)}
                  key={item.title}
                  variant='outline'
                  bg={item.hex}
                  borderWidth={activeBackgroundColor === item ? '2px' : undefined}
                  borderColor={activeBackgroundColor === item ? 'black' : undefined}
                  size='sm'
                  aria-label={item.title}
                  _hover={{
                    bg: item.hex,
                    borderColor: 'black',
                    borderWidth: '2px',
                  }}
                >
                  {' '}
                  <ChakraV3.Text fontWeight='semibold' fontSize='lg' lineHeight='normal'>
                    A
                  </ChakraV3.Text>
                </ChakraV3.IconButton>
              ))}
            </ChakraV3.HStack>
          </ChakraV3.Stack>
          <ChakraV3.Separator />
          <ChakraV3.Button onClick={() => reset()} size='sm' w='fit-content' variant='ghost'>
            <FaTimesCircle />
            Reset formatting
          </ChakraV3.Button>
        </ChakraV3.Stack>
      )}
    >
      <ChakraV3.IconButton
        as='div'
        tabIndex={0}
        bg={activeBackgroundColor.hex}
        rounded='lg'
        aria-label='text-colors'
        variant='ghost'
        size='sm'
      >
        <ChakraV3.Text
          fontWeight='semibold'
          fontSize='lg'
          lineHeight='normal'
          color={activeTextColor.hex}
        >
          A
        </ChakraV3.Text>
      </ChakraV3.IconButton>
    </FloatingPopover>
  );
};
